import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Typography,
  InputAdornment,
  IconButton,
  Alert,
  TextField,
  Checkbox,
  FormControlLabel,
  Card,
  CardContent,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  ArrowForward,
  Email,
  Lock,
} from '@mui/icons-material';
import { SessionManager } from '../../utils/sessionManager';

const REMEMBER_EMAIL_KEY    = 'saheeh_admin_remembered_email';
const REMEMBER_PASSWORD_KEY = 'saheeh_admin_remembered_password';
const REMEMBER_FLAG_KEY     = 'saheeh_admin_remember_me';

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rememberMe, setRememberMe] = useState(false);

  useEffect(() => {
    if (SessionManager.isLoggedIn()) {
      navigate('/');
    }
  }, [navigate]);

  useEffect(() => {
    const savedEmail = localStorage.getItem(REMEMBER_EMAIL_KEY);
    const savedPasswordEncoded = localStorage.getItem(REMEMBER_PASSWORD_KEY);
    const savedRememberMe = localStorage.getItem(REMEMBER_FLAG_KEY) === 'true';
    if (savedEmail && savedRememberMe) {
      setEmail(savedEmail);
      setRememberMe(true);
      if (savedPasswordEncoded) {
        try {
          setPassword(atob(savedPasswordEncoded));
        } catch {
          localStorage.removeItem(REMEMBER_PASSWORD_KEY);
        }
      }
    }
  }, []);

  useEffect(() => {
    document.body.classList.add('login-page-active');
    return () => {
      document.body.classList.remove('login-page-active');
    };
  }, []);

  const handleLogin = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setError('');
    if (!email || !password) {
      setError(t('login.errorBothFields'));
      return;
    }

    setLoading(true);
    try {
      const { supabase } = await import('../../services/supabaseClient');
      const { data, error: authError } = await supabase.auth.signInWithPassword({ email, password });

      if (authError || !data.user || !data.session) {
        const msg = (authError?.message || '').toLowerCase();
        if (msg.includes('invalid login credentials')) {
          setError(t('login.errorWrongPassword') || 'Invalid email or password');
        } else if (msg.includes('email not confirmed')) {
          setError('Email not confirmed. Check your inbox.');
        } else {
          setError(authError?.message || t('login.errorGeneric'));
        }
        return;
      }

      SessionManager.saveSession(
        data.session.access_token,
        '',
        { email: data.user.email, role: 'admin', uid: data.user.id },
      );

      if (rememberMe) {
        localStorage.setItem(REMEMBER_EMAIL_KEY, email);
        localStorage.setItem(REMEMBER_FLAG_KEY, 'true');
        try {
          localStorage.setItem(REMEMBER_PASSWORD_KEY, btoa(password));
        } catch { /* ignore */ }
      } else {
        localStorage.removeItem(REMEMBER_EMAIL_KEY);
        localStorage.removeItem(REMEMBER_PASSWORD_KEY);
        localStorage.removeItem(REMEMBER_FLAG_KEY);
      }

      navigate('/');
    } catch (err: any) {
      console.error('[ADMIN LOGIN] Exception:', err);
      setError(t('login.errorGeneric'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100%',
        background: 'radial-gradient(ellipse at center, #0F2A44 0%, #050B14 100%)',
        position: 'relative',
        overflow: 'auto',
        display: 'flex',
        alignItems: { xs: 'flex-start', sm: 'center' },
        justifyContent: 'center',
        padding: { xs: 1.5, sm: 2, md: 3 },
        py: { xs: 2, sm: 2.5, md: 3 },
      }}
    >
      <Box
        className="login-card-container"
        sx={{
          position: 'relative',
          zIndex: 10,
          width: '100%',
          maxWidth: '480px',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Card
          sx={{
            backgroundColor: 'rgba(10, 37, 64, 0.85)',
            backdropFilter: 'blur(40px) saturate(180%)',
            WebkitBackdropFilter: 'blur(40px) saturate(180%)',
            borderRadius: '10px',
            padding: { xs: 2, sm: 2.5, md: 3 },
            boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.5)',
            border: '1px solid rgba(0, 200, 150, 0.15)',
            animation: 'fadeInUp 0.8s ease-out 0.2s backwards',
            width: '100%',
            maxWidth: '100%',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <CardContent sx={{ p: 0, width: '100%', overflow: 'hidden', borderRadius: '10px' }}>
            <Typography
              sx={{
                textAlign: 'center',
                color: '#00C896',
                fontSize: '11px',
                letterSpacing: '2px',
                fontWeight: 400,
                mb: 2,
                textTransform: 'uppercase',
              }}
            >
              {t('login.adminSystem')}
            </Typography>

            <Box
              sx={{
                textAlign: 'center',
                mb: 2.5,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1.5,
              }}
            >
              <Box
                component="img"
                src="/saheeh_logo_w.svg"
                alt="Saheeh"
                sx={{
                  width: { xs: 130, sm: 150, md: 170 },
                  height: 'auto',
                  filter: 'drop-shadow(0 8px 24px rgba(0,0,0,0.5))',
                }}
              />
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography
                sx={{
                  color: '#ffffff',
                  fontSize: { xs: '22px', sm: '26px', md: '30px' },
                  fontWeight: 600,
                  mb: 0.5,
                }}
              >
                {t('login.welcomeBack')}
              </Typography>
              <Typography
                sx={{
                  color: 'rgba(255, 255, 255, 0.6)',
                  fontSize: '13px',
                }}
              >
                {t('login.signInSubtitle')}
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 2, borderRadius: '8px' }}>
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleLogin} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography
                  sx={{
                    color: 'rgba(255, 255, 255, 0.8)',
                    fontSize: '13px',
                    fontWeight: 500,
                    mb: 0.75,
                  }}
                >
                  {t('login.emailAddress')}
                </Typography>
                <TextField
                  fullWidth
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('login.emailPlaceholder') as string}
                  disabled={loading}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Email sx={{ color: '#00C896', fontSize: '20px' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '10px',
                      backgroundColor: 'rgba(255, 255, 255, 0.06)',
                      color: '#ffffff',
                      minHeight: '44px',
                      overflow: 'hidden',
                      '& fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.12)',
                        borderRadius: '10px',
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(0, 200, 150, 0.4)',
                        borderRadius: '10px',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#00C896',
                        borderWidth: '2px',
                        borderRadius: '10px',
                      },
                    },
                    '& .MuiInputBase-input': {
                      color: '#ffffff',
                      padding: '10px 14px',
                      '&::placeholder': {
                        color: 'rgba(255, 255, 255, 0.5)',
                        opacity: 1,
                      },
                    },
                  }}
                />
              </Box>

              <Box>
                <Typography
                  sx={{
                    color: 'rgba(255, 255, 255, 0.8)',
                    fontSize: '13px',
                    fontWeight: 500,
                    mb: 0.75,
                  }}
                >
                  {t('login.password')}
                </Typography>
                <TextField
                  fullWidth
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('login.passwordPlaceholder') as string}
                  disabled={loading}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock sx={{ color: '#00C896', fontSize: '20px' }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          size="small"
                          sx={{
                            color: '#00C896',
                            padding: '2px',
                            width: '24px',
                            height: '24px',
                            '& .MuiSvgIcon-root': {
                              fontSize: '14px',
                            },
                          }}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '10px',
                      backgroundColor: 'rgba(255, 255, 255, 0.06)',
                      color: '#ffffff',
                      minHeight: '44px',
                      overflow: 'hidden',
                      '& fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.12)',
                        borderRadius: '10px',
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(0, 200, 150, 0.4)',
                        borderRadius: '10px',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#00C896',
                        borderWidth: '2px',
                        borderRadius: '10px',
                      },
                    },
                    '& .MuiInputBase-input': {
                      color: '#ffffff',
                      padding: '10px 45px 10px 14px',
                      '&::placeholder': {
                        color: 'rgba(255, 255, 255, 0.5)',
                        opacity: 1,
                      },
                    },
                  }}
                />
              </Box>

              <Box>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      sx={{
                        color: 'rgba(255, 255, 255, 0.5)',
                        '&.Mui-checked': {
                          color: '#00C896',
                        },
                      }}
                    />
                  }
                  label={
                    <Typography sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '13px' }}>
                      {t('login.rememberMe')}
                    </Typography>
                  }
                />
              </Box>

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading}
                endIcon={loading ? null : <ArrowForward />}
                sx={{
                  py: 1.25,
                  borderRadius: '10px',
                  background: 'linear-gradient(135deg, #00C896 0%, #00A37A 100%)',
                  color: '#0A2540',
                  fontWeight: 700,
                  fontSize: '15px',
                  textTransform: 'none',
                  boxShadow: 'none',
                  overflow: 'hidden',
                  opacity: loading ? 0.85 : 1,
                  cursor: loading ? 'wait' : 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover:not(:disabled)': {
                    background: 'linear-gradient(135deg, #1AD4A6 0%, #00B387 100%)',
                    transform: 'scale(1.02)',
                    boxShadow: '0 8px 24px rgba(0, 200, 150, 0.35)',
                    borderRadius: '10px',
                  },
                  '&:disabled': {
                    background: 'linear-gradient(135deg, #00C896 0%, #00A37A 100%)',
                    color: '#0A2540',
                    opacity: 0.85,
                    borderRadius: '10px',
                  },
                }}
              >
                {loading ? t('login.signingIn') : t('login.signIn')}
              </Button>
            </Box>

            <Box
              sx={{
                mt: 1.5,
                textAlign: 'center',
                color: '#00C896',
                fontSize: '10px',
                letterSpacing: '1px',
              }}
            >
              v1.0.0 | ZATCA Compliant | Made for Saudi Restaurants | Powered by{' '}
              <Typography
                component="span"
                sx={{
                  color: '#00C896',
                  fontWeight: 600,
                }}
              >
                BlackLayers
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default LoginPage;
